import type { QuestionAnswerDto } from './testimonial';
import type { TrialStatus, CompanySize } from './trial';

/**
 * Promoter-facing actions a vendor can reorder and toggle on/off.
 * The list order IS the display order (promoter quick actions + onboarding).
 */
export type PromoterActionType = 'USE_CASE' | 'REVIEW' | 'REFERENCE_CALL' | 'VIDEO' | 'REFERRAL';

export interface PromoterActionSetting {
  type: PromoterActionType;
  enabled: boolean;
}

export const PROMOTER_ACTION_TYPES: PromoterActionType[] = [
  'USE_CASE',
  'REVIEW',
  'REFERENCE_CALL',
  'VIDEO',
  'REFERRAL',
];

export const DEFAULT_PROMOTER_ACTIONS: PromoterActionSetting[] = PROMOTER_ACTION_TYPES.map(
  (type) => ({ type, enabled: true })
);

/**
 * Null-safe normalization: vendors created before this feature have no saved
 * config (null/undefined), and a saved list may contain duplicates, unknown
 * types, or miss types added later. Always returns all known types exactly once,
 * in the saved order, with missing types appended as enabled.
 */
export function normalizePromoterActions(
  actions?: PromoterActionSetting[] | null
): PromoterActionSetting[] {
  if (!actions?.length) return DEFAULT_PROMOTER_ACTIONS.map((a) => ({ ...a }));
  const seen = new Set<PromoterActionType>();
  const result: PromoterActionSetting[] = [];
  for (const action of actions) {
    const type = action?.type;
    if (!type || !PROMOTER_ACTION_TYPES.includes(type) || seen.has(type)) continue;
    seen.add(type);
    result.push({ type, enabled: action.enabled !== false });
  }
  for (const type of PROMOTER_ACTION_TYPES) {
    if (!seen.has(type)) result.push({ type, enabled: true });
  }
  return result;
}

export interface VendorResponse {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle?: string | null;
  address?: string;
  email: string;
  firebaseUid: string;
  companyName?: string | null;
  businessIdentificationNumber?: string | null;
  vendorLinkedin?: string | null;
  companyLinkedin?: string | null;
  description?: string | null;
  phone?: string | null;
  streetAddress?: string | null;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
  billingContact?: {
    firstName: string;
    lastName: string;
    email: string;
    address?: string;
    phone: string;
    streetAddress?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  } | null;
  adminContact?: {
    firstName: string;
    lastName: string;
    email: string;
    address: string;
    phone: string;
  } | null;
  logo?: string | null;
  selectedProductId?: string | null;
  avatar?: string | null;
  status?: TrialStatus | null;
  joinedDate?: string | null;
  pageInfo?: VendorPageInfo | null;
  reviews?: unknown | null;
  balance?: number;
  tremendousFundingSourceId?: string | null;
  balanceLastUpdated?: string | null;
  onboardingCompleted?: boolean;
  pagePublished?: boolean;
  isPublic?: boolean;
  isFeatured?: boolean;
  featuredOrder?: number | null;
  industries?: VendorIndustry[];
  preferredIndustries?: string[];
  tourCompleted?: boolean;
  /** Order + visibility of promoter actions. Null for vendors that never customized it. */
  promoterActions?: PromoterActionSetting[] | null;
  // Trial fields
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  accountActivated?: boolean;
  activationRequested?: boolean;
  activationRequestDate?: string | null;
  activatedAt?: string | null;
  activatedBy?: string | null;
  signupSource?: string | null;
  isNewClient?: boolean;
  companySize?: CompanySize | null;
  referralSource?: string | null;
  lastLoginAt?: string | null;
  totalLoginCount?: number;
  /** Sales demo tenant — payouts/CRM blocked, excluded from admin aggregates. */
  isDemo?: boolean;
  onboardingChecklistCompleted?: string[];
  createdAt?: string | null;
}

export type VendorIndustry =
  | 'SAAS'
  | 'FINTECH'
  | 'HEALTH'
  | 'ECOMMERCE'
  | 'EDUCATION'
  | 'MARKETING'
  | 'REAL_ESTATE'
  | 'MANUFACTURING'
  | 'CONSULTING'
  | 'OTHER';

export const VENDOR_INDUSTRIES: VendorIndustry[] = [
  'SAAS',
  'FINTECH',
  'HEALTH',
  'ECOMMERCE',
  'EDUCATION',
  'MARKETING',
  'REAL_ESTATE',
  'MANUFACTURING',
  'CONSULTING',
  'OTHER',
];

export interface PublicVendorCard {
  id: string;
  slug?: string | null;
  companyName?: string | null;
  logo?: string | null;
  description?: string | null;
  averageRating: number;
  reviewCount: number;
  promoterCount: number;
  roiCount: number;
  industries: VendorIndustry[];
  isFeatured: boolean;
  featuredOrder?: number | null;
}

export interface PublicVendorListResponse {
  items: PublicVendorCard[];
  total: number;
  page: number;
  size: number;
}

export interface VendorProduct {
  name: string;
  description: string;
}

export interface VendorPageInfo {
  products?: VendorProduct[] | null;
  about?: string | null;
  googleReviewsUrl?: string | null;
  g2ReviewsUrl?: string | null;
  companyUrl?: string | null;
  pageSlug?: string | null;
  externalReviewProviders?: ExternalReviewProviderPublic[] | null;
}

export type ExternalReviewProviderType = 'GOOGLE_REVIEWS' | 'CAPTERRA' | 'G2' | 'TRUSTPILOT';

export interface ExternalReviewProviderPublic {
  provider: ExternalReviewProviderType;
  profileUrl?: string | null;
  averageRating?: number | null;
  totalReviews?: number | null;
  visible?: boolean;
}

export interface ExtractionJobStatus {
  id: string;
  provider: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  averageRating?: number | null;
  totalReviews?: number | null;
  profileUrl?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PublicVendorReview {
  id: string;
  promoterId: string;
  promoterName: string;
  promoterCompany?: string | null;
  rating?: number | null;
  comment?: string | null;
  productId?: string | null;
  videoUrl?: string | null;
  hasVideo: boolean;
  hasImage: boolean;
  featured?: boolean | null;
  verified?: boolean | null;
  vendorResponse?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UseCasePublicDto {
  id: string;
  promoterId: string;
  title?: string | null;
  description?: string | null;
  keyBenefits: string[];
  industry?: string | null;
  companySize?: string | null;
  content?: string | null;
  promoterName: string;
  promoterCompany?: string | null;
  createdAt: string;
  isFeatured: boolean;
}

export interface PublicVendorResponse {
  companyName?: string | null;
  name?: string | null;
  logo?: string | null;
  description?: string | null;
  pageInfo?: VendorPageInfo | null;
  reviews: PublicVendorReview[];
  useCases: UseCasePublicDto[];
  externalReviewProviders?: ExternalReviewProviderPublic[];
}

export interface VendorContactInput {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  phone: string;
  streetAddress?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

export interface VendorCreateRequest {
  first_name: string;
  last_name: string;
  job_title?: string;
  address: string;
  street_address?: string;
  zip_code?: string;
  city?: string;
  country?: string;
  email: string;
  phone?: string;
  vendor_linkedin?: string;
  company_linkedin?: string;
  password?: string;
  company_name?: string;
  business_identification_number?: string;
  description?: string;
  logo?: string;
  avatar?: string;
  status?: 'active' | 'inactive' | 'pending' | 'suspended' | 'trial';
  joined_date?: string;
  page_info?: { companyUrl?: string };
  billing_contact?: VendorContactInput;
  admin_contact?: VendorContactInput;
}

export interface VendorUpdateRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  address?: string;
  streetAddress?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  phone?: string;
  companyName?: string;
  businessIdentificationNumber?: string;
  vendorLinkedin?: string;
  companyLinkedin?: string;
  description?: string;
  billingContact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    address?: string;
    phone?: string;
    streetAddress?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  };
  adminContact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    address?: string;
    phone?: string;
  };
  logo?: string;
  selectedProductId?: string;
  avatar?: string;
  status?: string;
  joinedDate?: string;
  isPublic?: boolean;
  isFeatured?: boolean;
  featuredOrder?: number | null;
  industries?: VendorIndustry[];
}

// For Vendor App (Self Update)
export interface UpdateVendorRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  address?: string;
  companyName?: string;
  businessIdentificationNumber?: string;
  vendorLinkedin?: string;
  companyLinkedin?: string;
  description?: string;
  phone?: string;
  streetAddress?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  billingContact?: {
    firstName: string;
    lastName: string;
    email: string;
    address?: string;
    phone: string;
    streetAddress?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  };
  adminContact?: {
    firstName: string;
    lastName: string;
    email: string;
    address: string;
    phone: string;
  };
  logo?: string;
  selectedProductId?: string;
  avatar?: string;
  status?: string;
  joinedDate?: string;
  pageInfo?: VendorPageInfo;
  onboardingCompleted?: boolean;
  pagePublished?: boolean;
  isPublic?: boolean;
  isFeatured?: boolean;
  featuredOrder?: number | null;
  industries?: VendorIndustry[];
  preferredIndustries?: string[];
  tourCompleted?: boolean;
  /** Order + visibility of promoter actions. Null for vendors that never customized it. */
  promoterActions?: PromoterActionSetting[] | null;
}

export interface VendorDeleteResponse {
  id: string;
  message: string;
}

export interface VendorAnalyticsResponse {
  metrics: {
    totalPromoters: number;
    activePromoters: number;
    totalProspects: number;
    monthlyProspects: number;
    monthlyPromoters: number;
    conversionRate: number;
    totalRewards: number;
    monthlyRewards: number;
    promoterActivationRate: number;
    prospectResponseRate: number;
    successfulCalls: number;
    useCases: number;
  };
  promoterLevels: {
    level1: number;
    advocate: number;
    champion: number;
    ambassador: number;
  };
  monthlyData: Array<{
    month: string;
    promoters: number;
    prospects: number;
    rewards: number;
  }>;
  recentActivity: Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    time: string;
    color: string;
  }>;
}

export interface VendorAnalytics {
  customerId: string;
  monthlyData: Array<{ month: string; value: number; label: string }>;
  recentActivity: Array<{ id: string; type: string; description: string; date: string; icon?: string }>;
}

export type UseCaseAnswerType = 'one_selected' | 'many_selected' | 'free_text' | 'numeral' | 'product_select';

export interface QuestionResponse {
  id: string;
  question: string;
  choices: string[];
  order: number;
  answer_type?: UseCaseAnswerType;
  created_at: string;
  updated_at: string;
}

export interface QuestionDto {
  question: string;
  choices: string[];
  order: number;
  question_type?: string;
  answer_type?: UseCaseAnswerType;
}

export interface UpdateQuestionsRequest {
  questions: QuestionDto[];
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  phone?: string | null;
  assignedProspects: string[];
  stats: {
    totalProspects: number;
    activeProspects: number;
    convertedProspects: number;
    totalMeetings: number;
  };
  joinedDate: string;
  status: string;
}

export interface TopPerformerDto {
  promoterId: string;
  promoterName: string;
  promoterEmail?: string | null;
  promoterAvatar?: string | null;
  totalMeetings: number;
  totalProspects: number;
  totalRewards: number;
  conversionRate: number;
  prospectsReferred: number;
}

export interface RewardByTypeDto {
  type: string; // RewardActivityType
  count: number;
  totalAmount: number;
}

export interface TrendComparisonDto {
  currentMonth: number;
  lastMonth: number;
  change: number; // percentage change
}

export interface MonthlyTrendsDto {
  meetings: TrendComparisonDto;
  prospects: TrendComparisonDto;
  revenue: TrendComparisonDto;
  activePromoters: TrendComparisonDto;
}

export interface ContentPerformanceDto {
  featuredReviews: number;
  featuredReviewsThisMonth: number;
  videoTestimonials: number;
  videoTestimonialsThisMonth: number;
  useCases: number;
  useCasesThisMonth: number;
}

export interface DashboardStatsResponse {
  totalMeetings: number;
  activePromoters: number;
  totalProspects: number;
  conversionRate: number;
  readyForCallPromoters: number;
  totalRevenue: number;
  pendingTestimonials: number;
  topPerformers: TopPerformerDto[];
  rewardsByType: RewardByTypeDto[];
  monthlyTrends: MonthlyTrendsDto;
  contentPerformance: ContentPerformanceDto;
}

// Generate use case for vendor request (answer questions → GPT, no testimonial created)
export interface GenerateUseCaseForRequestRequest {
  promoter_id: string;
  answers: QuestionAnswerDto[];
  product_id?: string;
}

// Reference call frequency submissions (promoter submits, vendor approves)
export type ReferenceCallSubmissionStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ReferenceCallSubmissionResponse {
  id: string;
  promoterId: string;
  promoterName: string;
  promoterCompany?: string | null;
  reference_call_frequency: string;
  timezone?: string | null;
  status: ReferenceCallSubmissionStatus;
  submittedAt: string;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
}

export interface RejectReferenceCallSubmissionRequest {
  rejection_reason?: string | null;
}
